import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../components/ui";
import { getProductionOrderCostByOrderNo } from "../../api/cost";

function fmtMoney(n, currency = "TRY") {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

function fmtQty(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("tr-TR");
}

const TYPE_LABELS = {
  paper: "Kağıt",
  ink: "Boya",
  lak: "Lak",
  box: "Koli",
  shrink: "Shrink",
  label: "Etiket",
  other: "Diğer",
};

export default function CostReportPage() {
  const [orderNo, setOrderNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);

  async function onSearch() {
    if (!orderNo.trim()) {
      setError("İş emri numarası girin (örn: WO-8-60)");
      return;
    }
    setError("");
    setLoading(true);
    setReport(null);
    try {
      const data = await getProductionOrderCostByOrderNo(orderNo.trim());
      setReport(data);
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Maliyet hesaplanamadı");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Fiili Maliyet Raporu"
        subtitle="İş emri bazlı gerçek tüketim + parti fiyatından maliyet hesaplama"
      />

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="İş Emri No"
            placeholder="WO-8-60"
            value={orderNo}
            onChange={(e) => setOrderNo(e.target.value)}
            size="small"
            sx={{ flex: 1, maxWidth: 320 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
          />
          <Button
            variant="contained"
            onClick={onSearch}
            disabled={loading}
          >
            {loading ? "Hesaplanıyor..." : "Hesapla"}
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {report && (
        <>
          {/* Özet kartı */}
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">
                {report.order_no}
              </Typography>
              <Chip
                label={`Planlanan: ${fmtQty(report.planned_qty)} adet`}
                size="small"
              />
              {report.missing_cost_count > 0 && (
                <Chip
                  label={`${report.missing_cost_count} kalem fiyatsız`}
                  color="warning"
                  size="small"
                />
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Toplam Maliyet
                </Typography>
                {Object.entries(report.totals_by_currency || {}).length === 0 ? (
                  <Typography color="text.secondary">—</Typography>
                ) : (
                  Object.entries(report.totals_by_currency).map(([cur, val]) => (
                    <Typography key={cur} variant="h5" color="primary">
                      {fmtMoney(val, cur)}
                    </Typography>
                  ))
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Birim Ürün Maliyeti
                </Typography>
                {Object.entries(report.unit_cost_by_currency || {}).length === 0 ? (
                  <Typography color="text.secondary">—</Typography>
                ) : (
                  Object.entries(report.unit_cost_by_currency).map(([cur, val]) => (
                    <Typography key={cur} variant="h5" color="secondary">
                      {fmtMoney(val, cur)} / adet
                    </Typography>
                  ))
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Ham madde türü bazlı özet */}
          {report.by_material_type?.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Hammadde Türüne Göre Dağılım
              </Typography>
              <Grid container spacing={2}>
                {report.by_material_type.map((row, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={idx}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {TYPE_LABELS[row.raw_material_type] ||
                          row.raw_material_type}
                      </Typography>
                      <Typography variant="h6">
                        {fmtMoney(row.total, row.currency)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Detay tablosu */}
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1">
                Tüketim Detayı ({report.total_lines} satır)
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>İşlem</TableCell>
                    <TableCell>Malzeme Kodu</TableCell>
                    <TableCell>Malzeme Adı</TableCell>
                    <TableCell>Lot No</TableCell>
                    <TableCell align="right">Miktar</TableCell>
                    <TableCell>Birim</TableCell>
                    <TableCell align="right">Birim Fiyat</TableCell>
                    <TableCell align="right">Tutar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.items.map((it, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        bgcolor: it.has_cost ? undefined : "warning.light",
                      }}
                    >
                      <TableCell>{it.operation_code || "—"}</TableCell>
                      <TableCell>{it.material_code}</TableCell>
                      <TableCell>
                        {it.material_name}
                        {it.variant_display_name && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {it.variant_display_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <code>{it.lot_no}</code>
                      </TableCell>
                      <TableCell align="right">{fmtQty(it.qty)}</TableCell>
                      <TableCell>{it.uom}</TableCell>
                      <TableCell align="right">
                        {it.unit_cost !== null
                          ? fmtMoney(it.unit_cost, it.currency)
                          : <Chip label="Fiyat yok" color="warning" size="small" />}
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {it.line_cost !== null
                            ? fmtMoney(it.line_cost, it.currency)
                            : "—"}
                        </strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Stack>
  );
}
